/**
 * SPEC.md §3, §6, §9 — the topbar's Export menu, disabled since M0. M5.2
 * (issue #22) turns it into a real dropdown, mirroring `NewMenu.tsx`'s
 * shape and built on the same `useMenu` hook (M5.1, issue #21), starting
 * with SVG. M5.3 (issue #23) adds PNG/JPEG/WebP at 1x/2x/4x alongside it.
 *
 * Positioned at the topbar's right edge (unlike New's left-aligned
 * dropdown), so its menu opens right-aligned to stay on screen.
 */
import { useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import type { DiagramType } from '../lib/diagramTypes';
import { RASTER_FORMAT_META, RASTER_FORMATS, SCALE_OPTIONS, saveRaster, saveSvg, type RasterFormat, type Scale } from '../lib/export';
import { useMenu } from './useMenu';

interface ExportMenuProps {
  /** The render loop's current SVG markup (the last good render, dimmed or not) — null until the first successful render. */
  svg: string | null;
  diagramType: DiagramType | null;
  /** Called with a §9-style confirmation string ("Saved SVG.") after a successful export. */
  onExport: (message: string) => void;
}

export default function ExportMenu({ svg, diagramType, onExport }: ExportMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();
  const [transparent, setTransparent] = useState(false);

  function handleSaveSvg() {
    if (svg === null) return;
    saveSvg(svg, `${diagramType?.id ?? 'diagram'}.svg`, transparent);
    onExport('Saved SVG.');
    close();
  }

  // Rasterizing is async (Image decode + canvas draw); the menu closes
  // right away rather than waiting on it — matches how a real click-driven
  // download feels instant even though the pipeline technically isn't.
  // A rare failure (SPEC.md §6 gotcha 8's canvas-size edge case) shows no
  // confirmation — there's no error-surface built for this one path — but
  // is still logged, so it isn't silently invisible to whoever's debugging.
  function handleSaveRaster(format: RasterFormat, scale: Scale) {
    if (svg === null) return;
    close();
    void saveRaster(svg, `${diagramType?.id ?? 'diagram'}.${RASTER_FORMAT_META[format].extension}`, {
      format,
      scale,
      transparent,
    })
      .then((actualScale) => onExport(`Saved ${RASTER_FORMAT_META[format].label} at ${actualScale}×.`))
      .catch((err: unknown) => console.error('Raster export failed:', err));
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        disabled={svg === null}
        className="flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3 disabled:cursor-not-allowed disabled:text-ink-4 disabled:hover:bg-transparent"
      >
        <Download size={13} aria-hidden />
        Export
        <ChevronDown size={12} aria-hidden />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Export"
          className="absolute right-0 top-full z-10 mt-1 w-56 rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleSaveSvg}
            className="block w-full px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
          >
            Save SVG
          </button>

          <div className="my-1 border-t border-ink-3" />

          {RASTER_FORMATS.map((format) => (
            <div
              key={format}
              role="group"
              aria-label={`${RASTER_FORMAT_META[format].label} export scale`}
              className="flex items-center justify-between px-3 py-1.5 text-xs text-text-ink"
            >
              <span>{RASTER_FORMAT_META[format].label}</span>
              <div className="flex gap-1">
                {SCALE_OPTIONS.map((scale) => (
                  <button
                    key={scale}
                    type="button"
                    role="menuitem"
                    aria-label={`Save ${RASTER_FORMAT_META[format].label} at ${scale}×`}
                    onClick={() => handleSaveRaster(format, scale)}
                    className="rounded-sm px-1.5 py-0.5 text-mute hover:bg-ink-3 hover:text-text-ink"
                  >
                    {scale}×
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="my-1 border-t border-ink-3" />

          <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-text-ink">
            <input
              type="checkbox"
              checked={transparent}
              onChange={(e) => setTransparent(e.target.checked)}
              className="h-3 w-3"
            />
            Transparent background
          </label>
        </div>
      )}
    </div>
  );
}
